/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarService } from '@services/requests/housien-services/CalendarService';
import type {
  GoogleCalendarSettings,
  GoogleSettingsWritable,
} from '@services/requests/housien-services/CalendarService.calendars';

export type GoogleSettingsPatch = GoogleSettingsWritable;

export interface UseGoogleSettingsReturn {
  settings?: GoogleCalendarSettings;
  isLoading: boolean;
  isError: boolean;
  isSaving: boolean;
  save: (patch: GoogleSettingsPatch) => Promise<void>;
}

/** Query key for one user's Google settings in one agency. */
export const googleSettingsKey = (agencyId: string) =>
  ['agenda', 'google-settings', agencyId] as const;

/**
 * Reads and writes the signed-in user's Google Calendar settings.
 *
 * Every switch saves on its own: the screen has no Save button, so a patch
 * carries only the field that changed and the cache is refetched from the
 * server rather than guessed at locally.
 *
 * @param agencyId - Agency the settings belong to.
 * @param enabled - Whether there is a connection worth querying.
 * @returns The settings, their load state, and the save function.
 */
export const useGoogleSettings = (
  agencyId: string,
  enabled: boolean,
): UseGoogleSettingsReturn => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: googleSettingsKey(agencyId),
    queryFn: () => CalendarService.getGoogleSettings(agencyId),
    enabled: enabled && Boolean(agencyId),
  });

  const mutation = useMutation({
    mutationFn: (patch: GoogleSettingsPatch) =>
      CalendarService.updateGoogleSettings(agencyId, patch),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: googleSettingsKey(agencyId) }),
  });

  const save = useCallback(
    async (patch: GoogleSettingsPatch) => {
      await mutation.mutateAsync(patch);
    },
    [mutation],
  );

  return {
    settings: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    isSaving: mutation.isPending,
    save,
  };
};
